import type { IUser, ConfirmationUserDataType } from 'src/types';
import type { ICompany } from 'src/types/generalTypes';
import storage from 'src/utils/storage';
import http, { resetToken } from './http';
import type { ResponseType } from './http';

const authPath = '/auth';

type SignInPayloadType = IUser & { company: ICompany };
type SignInTokensPairType = { authToken: string; refreshToken: string };

const signIn = async (data: { phone: string; code: string; key: string }) => {
  const response = await http.post<ResponseType<SignInPayloadType, SignInTokensPairType>>(`${authPath}/sign-in`, data);

  storage.authToken.set(response.data.meta.authToken);
  storage.refreshToken.set(response.data.meta.refreshToken);
  resetToken();
  return response;
};

const signUp = async (data: {
  firstName: string;
  lastName: string;
  workspaceName: string;
  domain: string;
  email: string;
  phone: string;
  key: string;
}) => {
  const response = await http.post<ResponseType<IUser>>(`${authPath}/sign-up`, data);

  storage.authToken.set(response.data.meta.authToken);
  storage.refreshToken.set(response.data.meta.refreshToken);
  resetToken();
  return response;
};

const getMe = () => {
  return http.get<ResponseType<IUser>>(`${authPath}/me`);
};

const generateDomain = (data: { name: string }) => {
  return http.post<ResponseType<string>>(`${authPath}/domain`, data);
};

const checkDomainName = (domainName: string) => {
  return http.post<ResponseType<{ isExists: boolean }>>(`${authPath}/domain/${domainName}`);
};

const checkIsDomainCreated = (subdomain: string) => {
  return http.post(`${authPath}/check-availability-domain`, { subdomain });
};

const requestCode = (data: { phone: string }) => {
  return http.post<ResponseType<string>>(`${authPath}/phone/send`, data);
};

const confirmCode = (data: { code: string; key: string; phone: string }) => {
  return http.post<ResponseType<{ isTrue: boolean }>>(`${authPath}/phone/check-code`, data);
};

const confirmCodeForNewUser = (data: { confirmationToken: string; code: string }) => {
  return http.post<ResponseType>(`${authPath}/phone/confirm-new-user-phone`, data);
};
const getUnconfimedUser = (token: string) => {
  return http.get<ResponseType<ConfirmationUserDataType>>(`${authPath}/unconfirmed-user`, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
};

const confirmNewUser = (data: { code: string; key: string }, token: string) => {
  return http.post(`${authPath}/confirm-new-user`, data, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
};

const requestCodeWithCheckUser = (data: { phone: string }) => {
  return http.post<ResponseType<string>>(`${authPath}/phone/check-and-send`, data);
};

const getMyAccounts = () => {
  return http.get<ResponseType<IUser[]>>(`${authPath}/my-accounts`);
};

const changeCompany = (id: number) => {
  return http.post<ResponseType<IUser>>(`${authPath}/change-company`, { companyId: id });
};

export default {
  signIn,
  getMe,
  generateDomain,
  requestCode,
  confirmCode,
  checkDomainName,
  signUp,
  requestCodeWithCheckUser,
  getMyAccounts,
  changeCompany,
  confirmCodeForNewUser,
  getUnconfimedUser,
  confirmNewUser,
  checkIsDomainCreated,
};
